import React, { useEffect, useState } from 'react';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../contexts/ToastContext';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { clubSettingsAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessage';
import { SkeletonStatCards } from '../components/ui/Skeleton';
import { BoardPostingPolicy, BoardCommentPolicy } from '../types/clubSettings';
import AiSettingsPanel from '../components/ai/AiSettingsPanel';

const POSTING_OPTIONS: { value: BoardPostingPolicy; label: string; description: string }[] = [
  {
    value: BoardPostingPolicy.AdminsOnly,
    label: 'Csak az adminok',
    description: 'Az edzők, játékosok és szülők csak olvassák a falat.',
  },
  {
    value: BoardPostingPolicy.AdminsAndCoaches,
    label: 'Az adminok és az edzők',
    description: 'Az edző a saját korosztályainak írhat.',
  },
  {
    value: BoardPostingPolicy.Everyone,
    label: 'Mindenki',
    description:
      'Az edzőkön túl a játékosok és a szülők is írhatnak, a saját (gyermekük) korosztályának. Klubszintű és vendégeknek szóló bejegyzést továbbra is csak admin írhat, kitűzni pedig csak a stáb tud.',
  },
];

const COMMENT_OPTIONS: { value: BoardCommentPolicy; label: string; description: string }[] = [
  {
    value: BoardCommentPolicy.Nobody,
    label: 'Senki',
    description: 'A falhoz nem lehet hozzászólni, a korábbi hozzászólások olvashatók maradnak.',
  },
  {
    value: BoardCommentPolicy.StaffOnly,
    label: 'Csak a stáb',
    description: 'Adminok és edzők szólhatnak hozzá, a többiek csak olvassák.',
  },
  {
    value: BoardCommentPolicy.Everyone,
    label: 'Mindenki',
    description: 'Bárki hozzászólhat, aki látja a bejegyzést (a vendégek kivételével).',
  },
];

/**
 * Funkció-kapcsolók a klub egészére. A vizuális testreszabástól (név, szín,
 * címer - lásd /club-settings) szándékosan külön oldal: itt olyan
 * beállítások élnek, amik egy-egy modult teljesen ki-be kapcsolnak.
 */
const SystemSettings: React.FC = () => {
  usePageTitle('Rendszerbeállítások');
  const { showToast } = useToast();
  const { settings, loading, refreshSettings } = useClubSettings();

  const [savingMembershipFees, setSavingMembershipFees] = useState(false);
  const [warningDaysInput, setWarningDaysInput] = useState('');
  const [savingWarningDays, setSavingWarningDays] = useState(false);

  useEffect(() => {
    if (settings) setWarningDaysInput(String(settings.sportDoctorWarningDays ?? 60));
  }, [settings]);

  const handleWarningDaysSave = async (value: number) => {
    if (!settings) return;
    if (!Number.isInteger(value) || value < 7 || value > 365) {
      showToast('A figyelmeztetés 7 és 365 nap között lehet.', 'error');
      return;
    }

    setSavingWarningDays(true);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: settings.clubName,
        primaryColor: settings.primaryColor,
        sportDoctorWarningDays: value,
      });
      await refreshSettings();
      showToast(`Mostantól ${value} nappal a lejárat előtt jelez a rendszer.`, 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'A beállítást nem sikerült menteni.'), 'error');
    } finally {
      setSavingWarningDays(false);
    }
  };
  const [savingGuestBookings, setSavingGuestBookings] = useState(false);
  const [savingBoard, setSavingBoard] = useState(false);

  const handleBoardPolicyChange = async (
    change: { boardPostingPolicy?: BoardPostingPolicy; boardCommentPolicy?: BoardCommentPolicy }
  ) => {
    if (!settings) return;

    setSavingBoard(true);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: settings.clubName,
        primaryColor: settings.primaryColor,
        ...change,
      });
      await refreshSettings();
      showToast('Az üzenőfal beállítása elmentve.', 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'A beállítást nem sikerült menteni.'), 'error');
    } finally {
      setSavingBoard(false);
    }
  };

  const handleMembershipFeesToggle = async (enabled: boolean) => {
    if (!settings) return;

    setSavingMembershipFees(true);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: settings.clubName,
        primaryColor: settings.primaryColor,
        membershipFeesEnabled: enabled,
      });
      await refreshSettings();
      showToast(
        enabled
          ? 'Tagdíjszedés bekapcsolva - a Tagdíjak menüpont ismét látszik.'
          : 'Tagdíjszedés kikapcsolva - a Tagdíjak menüpont eltűnt mindenkinél.',
        'success'
      );
    } catch (err) {
      showToast(getErrorMessage(err, 'A beállítást nem sikerült menteni.'), 'error');
    } finally {
      setSavingMembershipFees(false);
    }
  };

  const [savingFeedback, setSavingFeedback] = useState(false);
  const [savingApplications, setSavingApplications] = useState(false);
  const [applicationIntro, setApplicationIntro] = useState('');
  const [applicationTerms, setApplicationTerms] = useState('');

  useEffect(() => {
    setApplicationIntro(settings?.applicationIntro ?? '');
    setApplicationTerms(settings?.applicationTerms ?? '');
  }, [settings?.applicationIntro, settings?.applicationTerms]);

  const handleFeedbackToggle = async (enabled: boolean) => {
    if (!settings) return;

    setSavingFeedback(true);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: settings.clubName,
        primaryColor: settings.primaryColor,
        trainingFeedbackEnabled: enabled,
      });
      await refreshSettings();
      showToast(
        enabled
          ? 'Edzés után a résztvevők értesítést kapnak, hogy értékeljék az edzést.'
          : 'Az edzés utáni visszajelzés kikapcsolva.',
        'success'
      );
    } catch (err) {
      showToast(getErrorMessage(err, 'A beállítást nem sikerült menteni.'), 'error');
    } finally {
      setSavingFeedback(false);
    }
  };

  const saveApplications = async (patch: { applicationsEnabled?: boolean; applicationIntro?: string; applicationTerms?: string }) => {
    if (!settings) return;

    setSavingApplications(true);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: settings.clubName,
        primaryColor: settings.primaryColor,
        ...patch,
      });
      await refreshSettings();
      showToast('Jelentkezési beállítások mentve.', 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'A beállítást nem sikerült menteni.'), 'error');
    } finally {
      setSavingApplications(false);
    }
  };

  const handleGuestBookingToggle = async (enabled: boolean) => {
    if (!settings) return;

    setSavingGuestBookings(true);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: settings.clubName,
        primaryColor: settings.primaryColor,
        guestEventCreationEnabled: enabled,
      });
      await refreshSettings();
      showToast(
        enabled
          ? 'A vendégek mostantól felvehetnek foglalást - minden jóváhagyásra vár.'
          : 'A vendégek nem vehetnek fel foglalást. A meglévők megmaradnak.',
        'success'
      );
    } catch (err) {
      showToast(getErrorMessage(err, 'A beállítást nem sikerült menteni.'), 'error');
    } finally {
      setSavingGuestBookings(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="py-6">
        <SkeletonStatCards count={1} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center mb-6">
        <AdjustmentsHorizontalIcon className="h-7 w-7 text-primary-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">Rendszerbeállítások</h1>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        Ezek a kapcsolók egy-egy funkciót teljesen ki- vagy bekapcsolnak a klub minden
        felhasználójánál. Csak adminok látják ezt az oldalt.
      </p>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden mb-6">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Sportorvosi figyelmeztetés</h2>
        </div>
        <div className="p-4 md:p-6 space-y-3">
          <p className="text-sm text-gray-500">
            Hány nappal a lejárat előtt jelezzen a rendszer: ennyi napon belül lejáró igazolások jelennek
            meg a főoldali widgetben, és ekkor megy az első emlékeztető email (utána 30 és 7 nappal előtte,
            majd lejáratkor).
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {[30, 60, 90].map((days) => (
              <button
                key={days}
                type="button"
                disabled={savingWarningDays}
                onClick={() => {
                  setWarningDaysInput(String(days));
                  handleWarningDaysSave(days);
                }}
                aria-pressed={settings.sportDoctorWarningDays === days}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                  settings.sportDoctorWarningDays === days
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {days} nap
              </button>
            ))}
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                handleWarningDaysSave(parseInt(warningDaysInput, 10));
              }}
            >
              <input
                type="number"
                inputMode="numeric"
                min={7}
                max={365}
                value={warningDaysInput}
                onChange={(e) => setWarningDaysInput(e.target.value)}
                aria-label="Figyelmeztetés napokban"
                className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-center text-base sm:text-sm"
              />
              <span className="text-sm text-gray-600">nap</span>
              <button
                type="submit"
                disabled={savingWarningDays || warningDaysInput === String(settings.sportDoctorWarningDays)}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {savingWarningDays ? 'Mentés…' : 'Mentés'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Tagdíjkezelés</h2>
        </div>
        <div className="p-4 md:p-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.membershipFeesEnabled}
              disabled={savingMembershipFees}
              onChange={(e) => handleMembershipFeesToggle(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">A klub szed tagdíjat</div>
              <div className="text-sm text-gray-500">
                Ha kikapcsolod, a Tagdíjak menüpont, a dashboard kintlévőség-widgetek és a havi
                tételek automatikus generálása is eltűnik / leáll. A már rögzített tagdíjadatok
                megmaradnak, csak elrejtjük őket - visszakapcsolás után minden ugyanúgy látszik.
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden mt-6">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Vendég foglalás</h2>
        </div>
        <div className="p-4 md:p-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.guestEventCreationEnabled}
              disabled={savingGuestBookings}
              onChange={(e) => handleGuestBookingToggle(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">A vendégek felvehetnek pályafoglalást</div>
              <div className="text-sm text-gray-500">
                A vendég (gondnok, kitmanager, külsős csapat) a saját nevére felvehet egy
                foglalást a naptárba. Minden ilyen foglalás jóváhagyásra vár: addig csak ő és
                az adminok látják, és nem foglalja a pályát. Kikapcsolva a már felvett
                foglalások megmaradnak.
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden mt-6">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Edzés utáni visszajelzés</h2>
        </div>
        <div className="p-4 md:p-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.trainingFeedbackEnabled}
              disabled={savingFeedback}
              onChange={(e) => handleFeedbackToggle(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">Kérdezzük meg a játékosokat, milyen volt az edzés</div>
              <div className="text-sm text-gray-500">
                Fél órával az edzés vége után a résztvevők (saját fiók híján a szüleik) értesítést kapnak:
                mennyire tetszett, mennyire volt nehéz (1-10), és írhatnak megjegyzést. Az edző az eseménynél
                látja az átlagokat. Kisebb korosztályoknál érdemes megfontolni, kell-e.
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden mt-6">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Jelentkezés új játékosoknak</h2>
        </div>
        <div className="p-4 md:p-6 space-y-4">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.applicationsEnabled}
              // Bekapcsolni csak elmentett adatkezelési tájékoztatóval lehet (kiskorúak adatai)
              disabled={savingApplications || (!settings.applicationsEnabled && !settings.applicationTerms?.trim())}
              onChange={(e) => saveApplications({ applicationsEnabled: e.target.checked })}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">Nyilvános jelentkezési űrlap</div>
              <div className="text-sm text-gray-500">
                Bejelentkezés nélkül kitölthető űrlap a klub weboldalára vagy plakátra:{' '}
                <span className="font-mono text-gray-700">{window.location.origin}/apply</span>. A jelentkezések a
                Jelentkezések menüpontba érkeznek; elfogadáskor létrejön a játékos, és a szülő meghívót kap.
              </div>
              {!settings.applicationsEnabled && !settings.applicationTerms?.trim() && (
                <div className="mt-1 text-sm text-amber-700">
                  Bekapcsolás előtt add meg és mentsd el lent az adatkezelési tájékoztatót.
                </div>
              )}
            </div>
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Bevezető szöveg az űrlap tetején
            <textarea
              rows={3}
              maxLength={2000}
              value={applicationIntro}
              onChange={(e) => setApplicationIntro(e.target.value)}
              placeholder="Pl. Szeretettel várjuk a 2014-2019-ben született fiúkat és lányokat. Az első két edzés ingyenes."
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Elfogadandó feltételek / adatkezelési tájékoztató
            <textarea
              rows={6}
              maxLength={20000}
              value={applicationTerms}
              onChange={(e) => setApplicationTerms(e.target.value)}
              placeholder="Kötelező: a jelentkezőnek el kell fogadnia (kiskorúak adatainál a szülői hozzájárulás miatt)."
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="mt-1 block text-xs font-normal text-gray-500">
              Az elfogadott szöveg másolata a jelentkezéssel együtt megmarad, így később is visszakereshető, mit fogadott el.
            </span>
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              disabled={savingApplications}
              onClick={() => saveApplications({ applicationIntro, applicationTerms })}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {savingApplications ? 'Mentés...' : 'Szövegek mentése'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden mt-6">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Üzenőfal</h2>
        </div>
        <div className="p-4 md:p-6 space-y-6">
          <fieldset disabled={savingBoard}>
            <legend className="font-medium text-gray-900 mb-2">Ki tehet közzé bejegyzést?</legend>
            <div className="space-y-3">
              {POSTING_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="board-posting-policy"
                    checked={settings.boardPostingPolicy === option.value}
                    onChange={() => handleBoardPolicyChange({ boardPostingPolicy: option.value })}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset disabled={savingBoard} className="border-t border-gray-100 pt-6">
            <legend className="font-medium text-gray-900 mb-2">Ki szólhat hozzá a bejegyzésekhez?</legend>
            <div className="space-y-3">
              {COMMENT_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="board-comment-policy"
                    checked={settings.boardCommentPolicy === option.value}
                    onChange={() => handleBoardPolicyChange({ boardCommentPolicy: option.value })}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-400">
              A bejegyzés írója ezen felül bejegyzésenként is kikapcsolhatja a hozzászólást.
            </p>
          </fieldset>
        </div>
      </div>

      <AiSettingsPanel />
    </div>
  );
};

export default SystemSettings;
